import React, { useState, useEffect } from 'react';
import FileUpload from './components/FileUpload';
import AnalysisResults from './components/AnalysisResults';
import ErrorDisplay from './components/ErrorDisplay';
import FeaturesSection from './components/FeaturesSection';
import Dashboard from './components/Dashboard';
import CampaignsList from './components/CampaignsList';
import CampaignForm from './components/CampaignForm';
import CampaignCompletionForm from './components/CampaignCompletionForm';
import BatchCampaignCompletion from './components/BatchCampaignCompletion';
import TeamManagement from './components/TeamManagement';
import ProductManagement from './components/ProductManagement';
import Reports from './components/Reports';
import Analytics from './components/Analytics';
import ApifyExtraction from './components/ApifyExtraction';
import FormulaManager from './components/FormulaManager';
import ProfileInfoData from './components/ProfileInfoData';
import ChangeRequestsPanel from './components/ChangeRequestsPanel';
import CronJobManager from './components/CronJobManager';
import CronCampaigns from './components/CronCampaigns';
import EmployeeManagement from './components/EmployeeManagement';
import LoginPage from './components/LoginPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { getApiUrl, apiRequest, getAuthHeaders } from './utils/api';
import './index.css';

function AppContent() {
  const { user, logout, isAdmin } = useAuth();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [campaignData, setCampaignData] = useState(null);
  const [batchCampaigns, setBatchCampaigns] = useState(null);
  const [profileCache, setProfileCache] = useState({});
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Load activeTab from localStorage on mount, default to 'apify'
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('activeTab');
    return saved || 'apify';
  });
  
  const [campaigns, setCampaigns] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  
  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);
  const getIsDesktop = () => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth >= 1024;
  };
  const [isDesktop, setIsDesktop] = useState(() => getIsDesktop());
  const handleProfileCacheUpdate = (username, info) => {
    if (!username || !info) return;
    try {
      const normalized = typeof info === 'string' ? JSON.parse(info) : info;
      setProfileCache(prev => ({ ...prev, [username]: normalized }));
    } catch (_) {}
  };

  // Initial data fetch
  useEffect(() => {
    fetchCampaigns();
    fetchDashboardData();
  }, []);

  // Fetch campaigns and dashboard data (initial)
  useEffect(() => {
    fetchCampaigns();
    fetchDashboardData();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(getIsDesktop());
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isDesktop) {
      setIsNavOpen(false);
    }
  }, [isDesktop]);

  const fetchCampaigns = async () => {
    try {
      const response = await apiRequest(`/campaigns`);
      if (!response.ok) {
        if (response.status === 401) {
          // Unauthorized - only logout if token is truly invalid
          // Don't auto-logout on network errors or temporary issues
          const errorData = await response.json().catch(() => ({}));
          if (errorData.error && errorData.error.includes('token')) {
            logout();
          }
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.campaigns);
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      // Don't show error for network issues - just log it
      if (!err.message.includes('Failed to fetch') && !err.message.includes('NetworkError')) {
        setError(`Failed to fetch campaigns: ${err.message}. Make sure backend server is running on port 5000.`);
      }
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await apiRequest(`/dashboard`);
      if (!response.ok) {
        if (response.status === 401) {
          // Unauthorized - only logout if token is truly invalid
          const errorData = await response.json().catch(() => ({}));
          if (errorData.error && errorData.error.includes('token')) {
            logout();
          }
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      if (data.success) {
        setDashboardData(data.dashboard);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      // Don't set error for dashboard, just log it
    }
  };

  const handleFileUpload = async (file) => {
    setIsLoading(true);
    setError(null);
    setAnalysisResult(null);
    setCampaignData(null);

    const formData = new FormData();
    
    // Handle dual file upload
    if (Array.isArray(file) && file.length === 2) {
      formData.append('engagement_file', file[0]);
      formData.append('views_file', file[1]);
    } else {
      // Handle single file upload
      formData.append('file', file);
    }

    try {
      // Build auth headers without Content-Type so browser can set multipart boundary
      const headers = getAuthHeaders();
      delete headers['Content-Type'];
      const response = await fetch(`${getApiUrl()}/upload`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        if (data.auto_updated) {
          // Campaign was automatically updated due to duplicate detection
          alert(`✅ ${data.message}`);
          // Refresh dashboard data to show updated campaign
          fetchDashboardData();
          // Reset state
          setAnalysisResult(null);
          setCampaignData(null);
          setActiveTab('dashboard'); // Go back to dashboard
        } else {
          setAnalysisResult(data.analysis);
          setCampaignData(data.campaign_data);
          setActiveTab('complete'); // Switch to completion form
        }
      } else {
        setError(data.error || 'Analysis failed');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCampaignCreate = async (campaignData) => {
    try {
      const response = await apiRequest(`/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(campaignData),
      });

      const data = await response.json();

      if (data.success) {
        // Refresh campaigns and dashboard after successful creation
        fetchCampaigns();
        fetchDashboardData();
        setActiveTab('campaigns');
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (err) {
      return { success: false, error: 'Network error: ' + err.message };
    }
  };

  const handleCampaignComplete = async (completeData) => {
    // If completeData is provided, it means the form already made the API call
    if (completeData) {
      // Just refresh data and redirect
      await fetchCampaigns();
      await fetchDashboardData();
      setActiveTab('campaigns');
      setAnalysisResult(null);
      setCampaignData(null);
      return { success: true };
    }

    // If no completeData, make the API call (for other use cases)
    try {
      const response = await apiRequest(`/campaigns/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(completeData),
      });

      const data = await response.json();

      if (data.success) {
        await fetchCampaigns();
        await fetchDashboardData();
        setActiveTab('campaigns');
        setAnalysisResult(null);
        setCampaignData(null);
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (err) {
      return { success: false, error: 'Network error: ' + err.message };
    }
  };

  const handleCancelCompletion = () => {
    setAnalysisResult(null);
    setCampaignData(null);
    setBatchCampaigns(null);
    setActiveTab('apify');
  };

  const handleBatchComplete = (result) => {
    // Refresh campaigns and dashboard after batch completion
    fetchCampaigns();
    fetchDashboardData();
    // Reset state and go back to campaigns tab
    setBatchCampaigns(null);
    setActiveTab('campaigns');
  };

  const handleCampaignDelete = async () => {
    // Refresh campaigns and dashboard after deletion
    await fetchCampaigns();
    await fetchDashboardData();
  };

  const handleApifyExtract = (data) => {
    if (data.duplicate_found) {
      // Campaign was automatically updated due to duplicate detection
      // Refresh campaigns and dashboard data to show updated campaign
      fetchCampaigns();
      fetchDashboardData();
      // Reset state and go back to campaigns tab
      setAnalysisResult(null);
      setCampaignData(null);
      setBatchCampaigns(null);
      setActiveTab('campaigns');
    } else if (data.multiple && data.campaigns) {
      // Multiple campaigns extraction - show batch completion form
      setBatchCampaigns(data.campaigns);
      setAnalysisResult(null);
      setCampaignData(null);
      setActiveTab('batch-complete');
    } else if (data.extracted_data) {
      // New extraction - show completion form
      setAnalysisResult(data.extracted_data);
      setCampaignData(data.campaign_data);
      setBatchCampaigns(null);
      // Cache influencer profile info from extraction for Profile Info page
      try {
        const raw = data?.campaign_data?.influencer_info;
        const info = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const uname = (info && (info.username)) || data?.campaign_data?.influencer_username || data?.campaign_data?.influencer_name;
        if (uname && info) {
          setProfileCache(prev => ({ ...prev, [uname]: info }));
        }
      } catch (_) {}
      setActiveTab('complete');
    }
  };

  const tabs = [
    { id: 'apify', label: 'URL Reel Extraction', icon: '🔗' },
    { id: 'complete', label: 'Complete Campaign', icon: '✅' },
    { id: 'campaigns', label: 'Campaigns', icon: '📋' },
    { id: 'profile-info', label: 'Profile Info Data', icon: '🧑‍💼' },
    { id: 'reports', label: 'Reports & Analytics', icon: '📈' },
    { id: 'analytics', label: 'Analytics Dashboard', icon: '📊' },
    { id: 'team', label: isAdmin() ? 'Team Management' : 'Team Directory', icon: '👥' },
    { id: 'products', label: 'Product Management', icon: '📦' },
    // Only show admin-only tabs for admin users
    ...(isAdmin() ? [
      { id: 'formula-manager', label: 'Formula Manager', icon: '🧮' },
      { id: 'change-requests', label: 'Change Requests', icon: '🔍' },
      { id: 'cron-jobs', label: 'Cron Jobs', icon: '⏰' },
      { id: 'cron-campaigns', label: 'Cron Campaigns', icon: '🔄' },
      { id: 'employee-management', label: 'Employee Management', icon: '👥' }
    ] : []),
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      {/* Panel Trigger */}
      {!isNavOpen && (
        <button
          onClick={() => setIsNavOpen(true)}
          className="fixed top-4 left-4 z-40 inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white/90 shadow border border-gray-200 text-gray-700 hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Open navigation"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Overlay */}
      {isNavOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-[1px] z-40"
          onClick={() => setIsNavOpen(false)}
        />
      )}

      <div className="lg:flex">
        <aside
          className={`fixed inset-y-0 left-0 z-50 w-64 bg-white/95 backdrop-blur-md shadow-xl border-r border-gray-200/60 flex flex-col transform transition-transform duration-300 ${
            isNavOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
        {/* Logo Section */}
        <div className="p-6 border-b border-gray-200/50">
          <div className="flex items-center group">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
              <span className="text-white font-bold text-lg">AI</span>
            </div>
            <div className="ml-3">
              <h1 className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Reel Analytics
              </h1>
              <p className="text-xs text-gray-500">AI-Powered Insights</p>
            </div>
            <button
              onClick={() => setIsNavOpen(false)}
              className="ml-auto text-gray-400 hover:text-gray-600"
              aria-label="Close navigation"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Navigation Menu */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {tabs.filter(tab => tab.id !== 'complete' || (analysisResult && campaignData)).map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setIsNavOpen(false); }}
              className={`group w-full flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg transform scale-105'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
              } gap-3`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <span className="text-lg group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                {tab.icon}
              </span>
              <span className="animate-fade-in-up flex-1 text-left">{tab.label}</span>
              {activeTab === tab.id && (
                <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse"></div>
              )}
            </button>
          ))}
        </nav>
        
        {/* User Info (Company auto-detected from email domain) */}
        <div className="p-4 border-t border-gray-200/50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mr-2">
                <span className="text-white text-xs font-bold">
                  {user?.role === 'admin' ? 'A' : 'E'}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{user?.username}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                {user?.company && (
                  <p className="text-xs text-gray-400 mt-1 capitalize">{user?.company}</p>
                )}
              </div>
            </div>
            <button
              onClick={logout}
              className="text-gray-400 hover:text-red-500 transition-colors duration-200 p-2 rounded-full hover:bg-gray-100"
              title="Logout"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
          <div className="text-xs text-gray-500 text-center">
            <p>Powered by AI Magic ✨</p>
            <p className="mt-1">v2.0 Enhanced</p>
          </div>
        </div>
        </aside>

        {/* Main Content */}
        <main
          className={`relative flex-1 pb-16 px-4 sm:px-8 lg:px-12 ${isDesktop ? 'pt-12' : 'pt-24'} transition-all duration-300`}
          style={{ marginLeft: isDesktop && isNavOpen ? '16rem' : 0 }}
        >
        {/* Floating button -> URL Reel Extraction */}
        <button
          onClick={() => setActiveTab('apify')}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group"
          title="Go to URL Reel Extraction"
        >
          <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8M7 17H5a2 2 0 01-2-2V5a2 2 0 012-2h10a2 2 0 012 2v2" />
          </svg>
        </button>
        {/* Page Content */}
        <div className="space-y-8">
          {/* Upload Screenshot removed. Global button to URL Reel Extraction provided below. */}

          {activeTab === 'apify' && (
            <div className="animate-fade-in-up">
              <ApifyExtraction onExtract={handleApifyExtract} isLoading={isLoading} />
            </div>
          )}

          {activeTab === 'dashboard' && (
            <div className="animate-fade-in-up">
              <Dashboard data={dashboardData} />
            </div>
          )}

          {activeTab === 'campaigns' && (
            <div className="animate-fade-in-up">
              <CampaignsList campaigns={campaigns} onCampaignDelete={handleCampaignDelete} />
            </div>
          )}

          {activeTab === 'create' && (
            <div className="animate-fade-in-up">
              <CampaignForm onSubmit={handleCampaignCreate} />
            </div>
          )}

          {activeTab === 'complete' && analysisResult && campaignData && (
            <div className="animate-fade-in-scale">
              <CampaignCompletionForm
                analysisResult={analysisResult}
                campaignData={campaignData}
                onProfileInfo={handleProfileCacheUpdate}
                onComplete={handleCampaignComplete}
                onCancel={handleCancelCompletion}
              />
            </div>
          )}

          {activeTab === 'batch-complete' && batchCampaigns && batchCampaigns.length > 0 && (
            <div className="animate-fade-in-scale">
              <BatchCampaignCompletion
                campaigns={batchCampaigns}
                onComplete={handleBatchComplete}
                onCancel={handleCancelCompletion}
              />
            </div>
          )}

          {activeTab === 'team' && (
            <div className="animate-fade-in-up">
              <TeamManagement />
            </div>
          )}

          {activeTab === 'profile-info' && (
            <div className="animate-fade-in-up">
              <ProfileInfoData campaigns={campaigns} profileCache={profileCache} />
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="animate-fade-in-up">
              <Reports />
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="animate-fade-in-up">
              <Analytics />
            </div>
          )}

          {activeTab === 'products' && (
            <div className="animate-fade-in-up">
              <ProductManagement />
            </div>
          )}

          {activeTab === 'formula-manager' && (
            <div className="animate-fade-in-up">
              <FormulaManager />
            </div>
          )}

          {activeTab === 'change-requests' && (
            <div className="animate-fade-in-up">
              <ChangeRequestsPanel />
            </div>
          )}

          {activeTab === 'cron-jobs' && (
            <div className="animate-fade-in-up">
              <CronJobManager />
            </div>
          )}

          {activeTab === 'cron-campaigns' && (
            <div className="animate-fade-in-up">
              <CronCampaigns />
            </div>
          )}

          {activeTab === 'employee-management' && (
            <div className="animate-fade-in-up">
              <EmployeeManagement />
            </div>
          )}
        </div>
      </main>
      </div>
    </div>
  );
}

function App() {
  const { user, isLoading, login } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-6"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLogin={login} />;
  }

  return <AppContent />;
}

export default function AppWrapper() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}